import { z } from "zod";

// TES-1106 (Emelie 2026-10-05) — the patient's voluntary consent to being
// contacted with the occasional question about how she experienced the
// service. Separate from every other consent: it is not marketing (there is
// no marketing consent), and it is not TES-433's purchase consent.
//
// The same evidence pattern as TES-433: the server stores which wording she
// saw (a version id), when, and an audit row for every change. Unlike the
// purchase consent it can be withdrawn and given again, so every change is a
// new row in an append-only history, never an overwrite.

/**
 * Every wording that has ever been shown, by version. The Swedish text is
 * Emelie's, verbatim; it is the evidence of what a stored version means.
 *
 * Changing the wording means ADDING a version here and moving
 * PRODUCT_FEEDBACK_CONSENT_VERSION to it, never editing an existing entry: a
 * stored row points at its version for good. An older app still showing an
 * older wording records that older version, honestly, instead of being
 * refused or recorded against text she never saw.
 */
export const PRODUCT_FEEDBACK_CONSENT_TEXTS = {
  "2026-10-05":
    "Ja, Ella får kontakta mig med enstaka frågor om hur jag upplevde tjänsten och hur den kan förbättras. Det är frivilligt och jag kan när som helst ändra mitt val.",
} as const;

export type ProductFeedbackConsentVersion =
  keyof typeof PRODUCT_FEEDBACK_CONSENT_TEXTS;

/** The wording in force, shown by the app and the web today. */
export const PRODUCT_FEEDBACK_CONSENT_VERSION: ProductFeedbackConsentVersion =
  "2026-10-05";

export function isProductFeedbackConsentVersion(
  v: unknown,
): v is ProductFeedbackConsentVersion {
  return (
    typeof v === "string" &&
    Object.prototype.hasOwnProperty.call(PRODUCT_FEEDBACK_CONSENT_TEXTS, v)
  );
}

/** Where the choice was made. */
export const PRODUCT_FEEDBACK_CONSENT_SOURCES = [
  // The app's e-mail step (TES-504 gate).
  "app_email_step",
  // The web's e-mail capture (/start, /welcome).
  "web_email_step",
  // The app's Profil.
  "app_profile",
] as const;
export const productFeedbackConsentSourceSchema = z.enum(
  PRODUCT_FEEDBACK_CONSENT_SOURCES,
);
export type ProductFeedbackConsentSource = z.infer<
  typeof productFeedbackConsentSourceSchema
>;

// PUT patient-api/v1/me/product-feedback-consent — the Profil choice.
export const setProductFeedbackConsentSchema = z.object({
  granted: z.boolean(),
  // What the client displayed; must be a known version.
  wordingVersion: z.string().min(1).max(40),
});
export type SetProductFeedbackConsent = z.infer<
  typeof setProductFeedbackConsentSchema
>;

// Her current choice, from the latest history row. null = she has never
// chosen, which counts as no consent.
export const productFeedbackConsentStateSchema = z.object({
  granted: z.boolean(),
  wordingVersion: z.string(),
  /** When this choice was made (ISO). */
  decidedAt: z.string(),
  source: productFeedbackConsentSourceSchema,
});
export type ProductFeedbackConsentState = z.infer<
  typeof productFeedbackConsentStateSchema
>;
